import {
  connectionConnecting,
  connectionError,
  connectionEstablished,
  connectionLost,
  connectionReconnecting,
  messageReceived,
  setSessionId,
} from '../../store/slices/websocketSlice';

// Message types
export const MSG_TYPE = {
  SEND_MESSAGE: 'send_message',
  MESSAGE: 'message',
  TYPING: 'typing',
  STOP_TYPING: 'stop_typing',
  READ: 'read',
  READ_RECEIPT: 'read_receipt',
  ONLINE_STATUS: 'online_status',
  PING: 'ping',
  PONG: 'pong',
  ERROR: 'error',
  CONNECTED: 'connected',
};

// Default configuration
const DEFAULT_CONFIG = {
  reconnectInterval: 1000,
  maxReconnectInterval: 30000,
  reconnectDecay: 1.5,
  maxReconnectAttempts: 10,
  pingInterval: 30000,
  messageQueueSize: 100,
  disconnectDebounceMs: 100,
};

class WebSocketService {
  constructor() {
    this.ws = null;
    this.config = DEFAULT_CONFIG;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.pingInterval = null;
    this.messageQueue = [];
    this.dispatch = null;
    this.url = null;
    this.token = null;
    this.explicitDisconnect = false;
    this.listeners = new Set();
    this.pendingDisconnectTimeout = null;
    this.getCurrentUserId = null; // Function to get current user ID from store
    this.getStore = null; // Store reference for reconnection
  }

  /**
   * Add message listener
   */
  addMessageListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * Remove message listener
   */
  removeMessageListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Initialize connection
   * @param {string} url - WebSocket URL
   * @param {string} token - Auth token
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} config - Config overrides
   */
  connect(url, token, dispatch, config = {}, getStore = null) {
    // Cancel pending disconnect if exists
    if (this.pendingDisconnectTimeout) {
      clearTimeout(this.pendingDisconnectTimeout);
      this.pendingDisconnectTimeout = null;
      console.log(
        '🔌 WebSocketService: Disconnect cancelled (reusing connection)'
      );
    }

    if (!url || !token) {
      console.warn('⚠️ WebSocketService: URL and token required');
      return;
    }

    // Reuse existing connection if same params and active
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING) &&
      this.url === url &&
      this.token === token
    ) {
      this.dispatch = dispatch; // Update dispatch just in case
      return;
    }

    this.url = url;
    this.token = token;
    this.dispatch = dispatch;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Set up function to get current user ID for filtering own messages
    if (getStore) {
      this.getStore = getStore;
      this.getCurrentUserId = () => getStore().auth?.user?.id;
    }
    this.explicitDisconnect = false;

    if (this.ws) {
      this.ws.close();
    }

    this.dispatch(connectionConnecting());
    console.log('🔌 WebSocketService: Connecting...');

    try {
      // Use cookie for auth to keep URL clean (avoid logging token)
      // This is more standard than Sec-WebSocket-Protocol hacks
      const isSecure = window.location.protocol === 'https:';
      document.cookie = `auth_token=${token}; path=/; samesite=strict; max-age=300${
        isSecure ? '; secure' : ''
      }`;
      this.ws = new WebSocket(url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      this.handleError(error);
    }
  }

  disconnect() {
    this.explicitDisconnect = true;
    // console.log('🔌 WebSocketService: Disconnecting (pending)...');

    // Clear previous pending disconnect
    if (this.pendingDisconnectTimeout) {
      clearTimeout(this.pendingDisconnectTimeout);
    }

    // Debounce disconnect to handle Strict Mode double-invocation
    this.pendingDisconnectTimeout = setTimeout(() => {
      this.performDisconnect();
    }, this.config.disconnectDebounceMs);
  }

  performDisconnect() {
    console.log('🔌 WebSocketService: Disconnecting (executing)...');

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.ws) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'User disconnect');
      }
      this.ws = null;
    }

    // Clear message queue on explicit disconnect
    this.messageQueue = [];

    if (this.dispatch) {
      this.dispatch(connectionLost());
    }
    this.reconnectAttempts = 0;
    this.pendingDisconnectTimeout = null;
  }

  handleOpen() {
    console.log('✅ WebSocketService: Connected');
    if (this.dispatch) {
      this.dispatch(connectionEstablished());
    }
    this.reconnectAttempts = 0;
    this.startPingInterval();
    this.flushMessageQueue();
  }

  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);

      if (message.type === MSG_TYPE.CONNECTED) {
        if (this.dispatch) {
          this.dispatch(setSessionId(message.payload?.session_id));
        }
        return;
      }
      if ([MSG_TYPE.PING, MSG_TYPE.PONG].includes(message.type)) {
        return;
      }

      // Notify listeners
      this.listeners.forEach((listener) => listener(message));

      if (this.dispatch) {
        this.dispatch(messageReceived(message));
      }

      switch (message.type) {
        case MSG_TYPE.ERROR:
          console.error('❌ WebSocketService: Server error:', message.payload);
          if (this.dispatch) {
            const errorPayload =
              typeof message.payload === 'string'
                ? { message: message.payload }
                : message.payload;
            this.dispatch(connectionError(errorPayload));
          }
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('❌ WebSocketService: Failed to parse message:', err);
    }
  }

  handleError(error) {
    console.error('❌ WebSocketService: Error:', error);
    if (this.dispatch) {
      const serializableError = {
        message: error.message || 'WebSocket Error',
        type: error.type,
      };
      this.dispatch(connectionError(serializableError));
    }
  }

  handleClose(event) {
    console.log(`🔌 WebSocketService: Disconnected (code: ${event.code})`);

    if (this.dispatch) {
      this.dispatch(connectionLost());
    }

    this.stopPingInterval();

    if (
      !this.explicitDisconnect &&
      event.code !== 1000 &&
      event.code !== 1001
    ) {
      this.scheduleReconnect();
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.log('❌ WebSocketService: Max reconnect attempts reached');
      return;
    }

    const delay = Math.min(
      this.config.reconnectInterval *
        Math.pow(this.config.reconnectDecay, this.reconnectAttempts),
      this.config.maxReconnectInterval
    );

    console.log(
      `🔄 WebSocketService: Reconnecting in ${delay}ms (attempt ${
        this.reconnectAttempts + 1
      })`
    );

    if (this.dispatch) {
      this.dispatch(connectionReconnecting());
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(
        this.url,
        this.token,
        this.dispatch,
        this.config,
        this.getStore
      );
    }, delay);
  }

  startPingInterval() {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRaw({ type: MSG_TYPE.PING });
      }
    }, this.config.pingInterval);
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  sendRaw(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  /**
   * Send a message (queues if offline)
   */
  sendMessage(type, payload) {
    const message = { type, payload };

    if (this.sendRaw(message)) {
      return true;
    }

    if (this.messageQueue.length < this.config.messageQueueSize) {
      this.messageQueue.push(message);
      console.log('📦 WebSocketService: Message queued (offline)');
    } else {
      console.warn('⚠️ WebSocketService: Message queue full, dropping message');
    }
    return false;
  }

  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (!this.sendRaw(message)) {
        this.messageQueue.unshift(message);
        break;
      }
    }
  }

  // --- Helper Methods ---

  sendChatMessage(content, options = {}) {
    const {
      conversationId,
      recipientId,
      contentType = 'text',
      tempId,
    } = options;

    return this.sendMessage(MSG_TYPE.SEND_MESSAGE, {
      conversation_id: conversationId,
      recipient_id: recipientId,
      content,
      content_type: contentType,
      temp_id: tempId || `temp_${Date.now()}`,
    });
  }

  sendTyping(conversationId, isTyping = true) {
    return this.sendMessage(isTyping ? MSG_TYPE.TYPING : MSG_TYPE.STOP_TYPING, {
      conversation_id: conversationId,
    });
  }

  sendReadReceipt(conversationId, messageId) {
    return this.sendMessage(MSG_TYPE.READ, {
      conversation_id: conversationId,
      message_id: messageId,
    });
  }
}

// Singleton instance
const websocketService = new WebSocketService();
export default websocketService;
